import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadState } from "../../constants/enums";
import { initialAiChatState, IChatMessage, IAiChatState } from "./aiChat.types";
import { fetchChatHistory, sendChatMessage } from "./aiChatThunk";
import { APIError } from "../../constants/types";

const aiChatSlice = createSlice({
  name: "aiChat",
  initialState: initialAiChatState,
  reducers: {
    // ─── Chat Window ───────────────────────────────────────────────────────────
    toggleChat: (state: IAiChatState) => {
      state.isOpen = !state.isOpen;
    },
    setChatOpen: (state: IAiChatState, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    clearMessages: (state: IAiChatState) => {
      state.messages = [];
      state.history = initialAiChatState.history;
    },

    // ─── Streaming (via Socket) ────────────────────────────────────────────────
    addUserMessage: (state: IAiChatState, action: PayloadAction<string>) => {
      state.messages.push({
        id: Date.now().toString(),
        role: "user",
        content: action.payload,
        timestamp: Date.now(),
      });
    },
    startAIMessage: (state: IAiChatState, action: PayloadAction<{ id: string }>) => {
      state.messages.push({
        id: action.payload.id,
        role: "ai",
        content: "",
        timestamp: Date.now(),
        isStreaming: true,
      });
      state.loading = true;
    },
    appendToAIMessage: (
      state: IAiChatState,
      action: PayloadAction<{ id: string; delta: string }>,
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message && message.role === "ai") {
        message.content += action.payload.delta;
      }
    },
    completeAIMessage: (
      state: IAiChatState,
      action: PayloadAction<{ id: string; final_output: string }>,
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message && message.role === "ai") {
        message.content = action.payload.final_output;
        message.isStreaming = false;
        message.error = false;
      }
      state.loading = false;
    },
    setAIMessageError: (
      state: IAiChatState,
      action: PayloadAction<{ id: string; message?: string }>,
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message && message.role === "ai") {
        if (action.payload.message) {
          message.content = action.payload.message;
        }
        message.error = true;
        message.isStreaming = false;
      }
      state.loading = false;
    },
    setLoading: (state: IAiChatState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    addAiMessage: (state: IAiChatState, action: PayloadAction<string>) => {
      state.messages.push({
        id: Date.now().toString(),
        role: "ai",
        content: action.payload,
        timestamp: Date.now(),
      });
    },
    // ─── File Context ─────────────────────────────────────────────────────────
    setFileContext: (state: IAiChatState, action: PayloadAction<string>) => {
      state.fileContext = action.payload;
      state.isOpen = true;
    },
    clearFileContext: (state: IAiChatState) => {
      state.fileContext = null;
    },
  },

  extraReducers: (builder) => {
    // ─── fetchChatHistory ──────────────────────────────────────────────────────
    builder
      .addCase(fetchChatHistory.pending, (state: IAiChatState) => {
        state.history.loading = LoadState.InProgress;
        state.history.error = null;
      })
      .addCase(fetchChatHistory.fulfilled, (state: IAiChatState, action: PayloadAction<{ messages: IChatMessage[]; hasMore: boolean; page: number }>) => {
        const { messages, hasMore, page } = action.payload;
        state.history.hasMore = hasMore;
        state.history.page = page;
        state.history.loading = LoadState.Loaded;

        if (page === 1) {
          // Initial load: replace all messages
          state.messages = messages;
        } else {
          // Pagination: prepend older messages without touching current chat
          state.messages = [...messages, ...state.messages];
        }
      })
      .addCase(fetchChatHistory.rejected, (state: IAiChatState, action: PayloadAction<APIError | undefined>) => {
        state.history.loading = LoadState.Failed;
        state.history.error =
          action.payload?.message ?? "Failed to load history.";
      });

    // ─── sendChatMessage ───────────────────────────────────────────────────────
    builder
      .addCase(sendChatMessage.pending, (state: IAiChatState) => {
        state.loading = true;
      })
      .addCase(sendChatMessage.rejected, (state: IAiChatState, action: PayloadAction<(APIError & { pendingId?: string }) | undefined>) => {
        const pendingId = action.payload?.pendingId;
        
        let targetMsg = null;
        if (pendingId) {
          targetMsg = state.messages.find(m => m.id === pendingId);
        } else {
          // Fallback: Mark the last AI placeholder as an error
          targetMsg = [...state.messages]
            .reverse()
            .find((m) => m.role === "ai" && m.isStreaming);
        }

        if (targetMsg) {
          targetMsg.error = true;
          targetMsg.isStreaming = false;
          targetMsg.content =
            action.payload?.message ?? "Request failed. Please try again.";
        }
        state.loading = false;
        // Clean up global pending ID on error
        if ((window as any).__pendingAiMessageId__ === pendingId) {
          (window as any).__pendingAiMessageId__ = null;
        }
      });
  },
});

export const {
  toggleChat,
  setChatOpen,
  clearMessages,
  addUserMessage,
  startAIMessage,
  appendToAIMessage,
  completeAIMessage,
  setAIMessageError,
  setLoading,
  addAiMessage,
  setFileContext,
  clearFileContext,
} = aiChatSlice.actions;

export const aiChatReducer = aiChatSlice.reducer;
